// The facts row of a practitioner's "Get to know" block, as pure functions.
//
// NO DICTIONARY ENTRY PER VALUE. Every fact below is formatted by the browser's own `Intl`
// in the VISITOR's language: a language code becomes "French" for an English reader (and the
// French or Spanish word for it for the others), an ISO instant becomes "Dec 2025", and a
// past instant becomes "3 days ago". The practice stores a code or an instant; the reader's
// locale decides the words. That is why the backend stores languages as ISO 639 codes and never as names.
//
// Every function answers "" for a value it cannot state, and the caller renders no row for
// "": an empty fact is left out, never shown as "—" or as a guess.
import { countryLabel, specialtyLabel } from "../i18n/practiceTaxonomy";

/**
 * The languages the profile editor offers, as ISO 639 codes. A SUGGESTION list, not the
 * accepted set: the API takes any well-formed code, and a code already saved that is not
 * here stays selectable in the editor. Chosen for the platform's practices — the three
 * product languages first, then the languages of the countries in `practiceTaxonomy`.
 */
export const SPOKEN_LANGUAGE_CODES = [
  "fr", "en", "es", "de", "it", "pt", "nl", "ar", "wo", "hi", "zh", "ru", "tr", "pl", "ro",
];

const safe = (fn) => {
  try { return fn(); } catch { return ""; }
};

/** "French" for "fr" to an English reader, the French word to a French one. The code itself
 * if Intl cannot name it. */
export const languageName = (code, lang) =>
  safe(() => new Intl.DisplayNames([lang], { type: "language" }).of(code)) || code;

/** "French and English" — the list joined the way the visitor's language joins lists. */
export const languagesText = (codes, lang) => {
  const names = (Array.isArray(codes) ? codes : []).filter(Boolean).map((c) => languageName(c, lang));
  if (names.length === 0) return "";
  return safe(() => new Intl.ListFormat([lang], { style: "long", type: "conjunction" }).format(names))
    || names.join(", ");
};

const instant = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** "Dec 2025" — the month the practice joined, never the day (Fiverr's "Member since"). */
export const memberSinceText = (iso, lang) => {
  const d = instant(iso);
  if (!d) return "";
  return safe(() => new Intl.DateTimeFormat(lang, { month: "short", year: "numeric" }).format(d));
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * "3 days ago" / "last week" / "2 months ago". Coarse on purpose: this row says how ACTIVE
 * the practice is, not when a particular client was seen, so it never names an hour. A
 * future instant (clock skew, a bad row) states nothing rather than "in 2 days".
 */
export const lastSessionText = (iso, lang, now = Date.now()) => {
  const d = instant(iso);
  if (!d) return "";
  const days = Math.floor((now - d.getTime()) / DAY_MS);
  if (days < 0) return "";
  const rtf = safe(() => new Intl.RelativeTimeFormat(lang, { numeric: "auto" }));
  if (!rtf) return "";
  if (days < 7) return rtf.format(-days, "day");
  if (days < 60) return rtf.format(-Math.floor(days / 7), "week");
  if (days < 730) return rtf.format(-Math.floor(days / 30), "month");
  return rtf.format(-Math.floor(days / 365), "year");
};

/** The country, localized when it is a catalogue value, verbatim when the practice typed it. */
export const countryText = (value, lang) => (value ? countryLabel(value, lang) : "");

/**
 * The line under the name — Fiverr's "Professional Video Editor and Youtuber". The
 * practitioner's own headline when they wrote one; otherwise their primary specialty,
 * localized. Never both, never a placeholder.
 */
export const headlineText = (profile, lang) =>
  (profile?.tagline || "").trim() || (profile?.specialty ? specialtyLabel(profile.specialty, lang) : "");
